<template>
	<div
		class="preview"
		:class="{ show: previewVisible }"
		:id="previewName"
		:ref="previewName"
		tabindex="-1"
		@focusout="removeTransformers(true), exportMainImageToStore()"
	></div>
</template>

<script>
import { mapState } from "vuex";
import debounce from "tiny-debounce";
export default {
	name: "Preview",
	data() {
		return {
			stage: null,
			layer: null,
			groups: {},
			images: {},
			allPreviewElementsCreated: false,
			exportImageSize: 500,
			stageSize: {
				width: 1000,
				height: 1000
			}
		};
	},
	props: ["preview"],
	computed: {
		...mapState({
			config: state => state.configurator.config,
			selectedOptions: state => state.configurator.selectedOptions,
			currentStepId: state => state.configurator.currentStep
		}),
		activeSteps: function() {
			return this.$store.getters["configurator/activeSteps"];
		},
		isMainPreview: function() {
			return this.preview.main || false;
		},
		previewVisible: function() {
			return this.previewInStep === this.preview.id;
		},
		previewInStep: function() {
			let preview = this.activeSteps.filter(step => step.id === this.currentStepId)[0];
			let firstPreview = this.config.previews[0].id;
			return preview.preview || firstPreview; // Fallback to first preview defined in config json
		},
		previewName: function() {
			return "configurator-preview-" + this.preview.id;
		},
		previewElements: function() {
			// Get preview elements containing input value
			return this.$store.getters["configurator/fullPreviewElements"][this.preview.id];
		}
	},
	created: function() {
		// Add preview to store
		this.$store.dispatch("configurator/addPreview", this.preview);

		// Listen viewport resize
		let self = this;
		window.addEventListener(
			"resize",
			debounce(function() {
				self.resizeStage();
			}, 250)
		);
	},
	mounted() {
		// Disable Konva warnings
		// - designArea group causes warnings, when transformers are in a layer instead of a group
		Konva.showWarnings = false;
		this.createStage();
		this.createPreviewElements();
	},
	watch: {
		selectedOptions: {
			handler() {
				if (this.allPreviewElementsCreated) {
					this.updatePreviewElements();
				}
			},
			deep: true
		}
	},
	methods: {
		exportMainImageToStore() {
			if (this.isMainPreview) {
				let dataURL = this.exportStage();
				this.$store.dispatch("configurator/setExportedImage", dataURL);
			}
		},
		exportStage() {
			return this.stage.toDataURL({
				pixelRatio: this.exportImageSize / this.stage.getWidth()
			});
		},
		resizeStage(size) {
			let previewElement = this.$refs[this.previewName];
			let smallerStageSize = this.stageSize.width > this.stageSize.height ? this.stageSize.height : this.stageSize.width;
			let smallerSize = previewElement
				? previewElement.clientWidth < previewElement.clientHeight
					? previewElement.clientWidth
					: previewElement.clientHeight > 0
					? previewElement.clientHeight
					: previewElement.clientWidth
				: smallerStageSize;
			let w = size || smallerSize;
			let scale = w / smallerStageSize;
			this.stage.setWidth(w);
			this.stage.setHeight(w);
			this.stage.scale({ x: scale, y: scale });
			this.updatePreviewElements();
		},
		async createPreviewElements() {
			if (this.previewElements) {
				for (let key of Object.keys(this.previewElements)) {
					await this.createPreviewElement(this.previewElements[key]);
				}
				this.resizeStage();
				this.layer.batchDraw();
				this.allPreviewElementsCreated = true;
			}
		},
		async updatePreviewElements() {
			if (this.previewElements) {
				for (let key of Object.keys(this.previewElements)) {
					await this.updatePreviewElement(this.previewElements[key]);
				}
				this.layer.batchDraw();
				this.exportMainImageToStore();
			}
		},
		createStage: function() {
			// Create stage
			this.stage = new Konva.Stage({
				container: this.previewName,
				width: this.stageSize.width,
				height: this.stageSize.height
			});

			// Create and add layer
			this.layer = new Konva.Layer();
			this.stage.add(this.layer);

			let self = this;

			// Add click events (transformable)
			this.stage.on("click tap", function(e) {
				// Remove old transformers
				self.removeTransformers();

				// If click on empty area - only redraw
				if (e.target === self.stage || !e.target.attrs.transformable) {
					self.layer.draw();
					return;
				}

				// Move element on top
				// e.target.moveToTop(); // Disabled to prevent monogram going over handles

				// Create new transformer
				var tr = new Konva.Transformer({
					// https://konvajs.org/api/Konva.Transformer.html
					anchorSize: 20
				});
				self.layer.add(tr);
				tr.attachTo(e.target);

				self.layer.draw();
			});
		},
		removeTransformers(redraw) {
			this.stage.find("Transformer").destroy();
			if (redraw) this.layer.draw();
		},
		loadElement: async function(el) {
			let self = this;
			return new Promise(async function(resolve, reject) {
				if (el.type == "ImageFromUrl") {
					Konva.Image.fromURL(el.src, function(node) {
						node.setAttrs(el.config);
						resolve(node);
					});
				} else if (el.config.fillPatternImage) {
					let imageObject = await self.loadImageFromUrl(el.config.fillPatternImage).catch(function(e) {
						console.error(e);
						reject("Could not load fillPatternImage " + el.config.fillPatternImage);
					});
					el.config.fillPatternImage = imageObject.image;
					var element = new Konva[el.type](el.config);
					resolve(element);
				} else {
					var element = new Konva[el.type](el.config);
					resolve(element);
				}
			});
		},
		createPreviewElement: async function(el) {
			if (!el.config) {
				return;
			}
			// Create element if it does not already exist
			let existingElement = this.layer.find("#" + el.config.id)[0];

			if (!existingElement) {
				let element = await this.loadElement(el);

				// If element has a group, add it there
				if (this.groups[el.group]) {
					// Add element to group
					this.groups[el.group].add(element);
				}
				// Otherwise add it to the layer
				else {
					// Add element to layer
					this.layer.add(element);
				}

				// If element is a group, add it to the groups list
				if (element.getClassName() === "Group") {
					this.groups[element.getAttr("id")] = element;
				}

				// Set mutated values
				await this.updatePreviewElement(el);
			}
		},
		updatePreviewElement: async function(el) {
			var targetElement = this.layer.find("#" + el.config.id)[0];
			// If element exists, update values
			if (targetElement) {
				let mutated = this.previewElements[el.id].mutated;
				if (mutated) {
					let self = this;
					// Loop each mutation item
					for (let node of Object.keys(mutated)) {
						// If mutation item is image or fillPatternImage with an url
						if (["image", "fillPatternImage"].includes(node) && mutated[node] && mutated[node].length > 0) {
							// Load image
							let imageObject = await this.loadImageFromUrl(mutated[node]).catch(function(e) {
								console.error(e);
								return false;
							});

							// Skip updating image, if image not found
							if (!imageObject) {
								continue;
							}

							// Resize image, if it's too big for the stage
							if (imageObject.size) {
								self.resizePreviewImageElement(self.previewElements[el.id].config, targetElement, imageObject.size);
							}

							// Make the image visible
							targetElement.setAttr("visible", true);

							// Add new image to the element
							targetElement.setAttr(node, imageObject.image);
						} 
						// If fontFamily is mutated, wait for the font to load
						else if (node === "fontFamily" && document.fonts) {
							console.log('font family');
							console.log(document.fonts);
							await document.fonts.load('1rem "'+mutated[node]+'"').then(() => { 
								// Font loaded, update value
								targetElement.setAttr(node, mutated[node]);
							});
						}
						// Scale text to fit
						else if (node === "text" && el.config.width && el.config.scaleToFit) {

							// Update text
							targetElement.setAttr(node, mutated[node]);

							const expansion = 1000;
							const configuredWidth = el.config.width;
							const align = targetElement.align();
							const textWidth = targetElement.getTextWidth();
							const scale = textWidth > configuredWidth ? configuredWidth/textWidth : 1;

							// Increase width of the text area
							targetElement.setWidth(configuredWidth + expansion);

							const elementWidth = targetElement.width();
							const elementHeight = targetElement.height();
							
							// Center text vertically
							targetElement.setOffsetY(elementHeight/2);
							targetElement.setY(el.config.y + elementHeight/2);

							if (align === "center") {
								// Align text to center
								targetElement.setOffsetX(elementWidth/2);
								targetElement.setX(el.config.x + elementWidth/2 - expansion/2);
							}
							if (align === "right") {
								// Align text to right
								targetElement.setOffsetX(elementWidth);
								targetElement.setX(el.config.x + elementWidth - expansion);
							}

							// Scale text
							targetElement.scale({ x: scale, y: scale });
							
						}
						// For other mutations
						else {
							// Update value
							targetElement.setAttr(node, mutated[node]);
						}
					}
				}
			}
		},
		loadImageFromUrl(src) {
			let self = this;
			return new Promise(function(resolve, reject) {
				// If image has already been loaded to component, return it.
				if (self.images[src]) {
					resolve({ image: self.images[src] });
				}
				// Otherwise load it
				else {
					self.images[src] = new Image();
					self.images[src].onload = function() {
						resolve({
							image: self.images[src],
							size: { width: this.width, height: this.height }
						});
					};
					self.images[src].onerror = function() {
						delete self.images[src];
						reject("Could not load image " + src);
					};
					self.images[src].crossOrigin = "Anonymous";
					self.images[src].src = src;
				}
			});
		},
		resizePreviewImageElement(originalConfig, targetElement, size) {
			let original = {
					width: originalConfig.width,
					height: originalConfig.height,
					scaleX: originalConfig.scaleX,
					scaleY: originalConfig.scaleY
				},
				width = size.width,
				height = size.height;

			// If image is too big, resize it
			if (width > this.stageSize.width || height > this.stageSize.height) {
				let orientation = width > height ? "landscape" : "portrait";

				// Too wide
				if (orientation === "landscape" && width > this.stageSize.width) {
					height = (height / width) * this.stageSize.width;
					width = this.stageSize.width;
				}
				// Too tall
				if (orientation === "portrait" && height > this.stageSize.height) {
					width = (width / height) * this.stageSize.height;
					height = this.stageSize.height;
				}
			}

			if (!original.width) targetElement.setAttr("width", width);
			if (!original.height) targetElement.setAttr("height", height);
			if (!original.scaleX) targetElement.setAttr("scaleX", 1);
			if (!original.scaleY) targetElement.setAttr("scaleY", 1);
		}
	}
};
</script>

<style scoped lang="scss">
.preview {
	height: 100%;
	width: 100%;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	opacity: 0;
	overflow: hidden;
	transition: opacity 500ms;
	z-index: 0;

	&:focus {
		outline: none;
	}

	.konvajs-content {
		margin: 0 auto;
	}

	&.show {
		opacity: 1;
		z-index: 1;
	}

	@media only screen and (min-width: $viewport-desktop) {
		display: flex;
		align-items: center;
	}
}
</style>
<style lang="scss">
.preview .konvajs-content {
	margin: 0 0 0 auto;
}
</style>
